import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Tabs,
  Tab,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useIsMobile } from '../../hooks/useResponsive';

/**
 * ResponsiveTabsAccordion - Shows tabs on desktop, accordions on mobile
 * @param {Array} tabs - Array of tab objects: [{ label, icon, content }]
 * @param {Number} value - Current tab index (for controlled component)
 * @param {Function} onChange - Callback when tab changes
 * @param {Object} tabsProps - Additional props for Tabs component
 * @param {Boolean} defaultAllClosed - If true, all accordions start closed on mobile (default: true)
 */
const ResponsiveTabsAccordion = ({
  tabs = [],
  value,
  onChange,
  tabsProps = {},
  defaultAllClosed = true,
}) => {
  const isMobile = useIsMobile();
  const [expanded, setExpanded] = useState(defaultAllClosed ? false : 0);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
    if (isExpanded && onChange) {
      onChange(event, panel);
    }
  };

  // Mobile view - Accordions
  if (isMobile) {
    return (
      <Box sx={{ width: '100%' }}>
        {tabs.map((tab, index) => (
          <Accordion
            key={index}
            expanded={expanded === index}
            onChange={handleAccordionChange(index)}
            sx={{
              mb: 1,
              '&:before': { display: 'none' }, // Remove default divider
              boxShadow: 1,
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                minHeight: 56,
                '&.Mui-expanded': {
                  minHeight: 56,
                  bgcolor: 'action.selected',
                },
                '& .MuiAccordionSummary-content': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                },
              }}
            >
              {tab.icon && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: expanded === index ? 'primary.main' : 'text.secondary',
                  }}
                >
                  {tab.icon}
                </Box>
              )}
              <Typography
                sx={{
                  fontWeight: expanded === index ? 600 : 400,
                  color: expanded === index ? 'primary.main' : 'text.primary',
                }}
              >
                {tab.label}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 2 }}>
              {tab.content}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    );
  }

  // Desktop view - Tabs
  return (
    <Box sx={{ width: '100%' }}>
      <Tabs
        value={value}
        onChange={onChange}
        variant="scrollable"
        scrollButtons="auto"
        {...tabsProps}
      >
        {tabs.map((tab, index) => (
          <Tab
            key={index}
            label={tab.label}
            icon={tab.icon}
            iconPosition="start"
          />
        ))}
      </Tabs>
      <Box sx={{ pt: 3 }}>
        {tabs[value]?.content}
      </Box>
    </Box>
  );
};

ResponsiveTabsAccordion.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
      content: PropTypes.node.isRequired,
    })
  ).isRequired,
  value: PropTypes.number,
  onChange: PropTypes.func,
  tabsProps: PropTypes.object,
  defaultAllClosed: PropTypes.bool,
};

export default ResponsiveTabsAccordion;
